/**
 * Client-side thumbnail generation for staged page images. Downscales each
 * picked `File` onto a canvas (rather than rendering the raw file) so the DOM
 * never holds many full-resolution phone photos.
 */

// ~2x the on-screen thumbnail box (~250px in the design) for crisp rendering
// on HiDPI displays without keeping full-res bitmaps around.
const THUMB_MAX_EDGE = 520;
const THUMB_JPEG_QUALITY = 0.8;

function drawToDataUrl(
  source: ImageBitmap | HTMLImageElement,
  width: number,
  height: number
): string {
  const scale = Math.min(1, THUMB_MAX_EDGE / Math.max(width, height));
  const w = Math.max(1, Math.round(width * scale));
  const h = Math.max(1, Math.round(height * scale));

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('2D canvas context unavailable');
  }
  ctx.drawImage(source, 0, 0, w, h);
  return canvas.toDataURL('image/jpeg', THUMB_JPEG_QUALITY);
}

function loadImageElement(objectUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to decode image'));
    img.src = objectUrl;
  });
}

/**
 * Produce a small, display-ready thumbnail data URL for `file`.
 *
 * Prefers `createImageBitmap` (fast, off-thread decode, honors EXIF
 * orientation via `imageOrientation: 'from-image'`), falling back to an
 * `<img>` element. If decoding/downscaling fails entirely, returns a raw
 * object URL so the page still shows *something*; callers should revoke the
 * returned URL when done (revoking a data URL is a harmless no-op).
 */
export async function makeThumbnailUrl(file: File): Promise<string> {
  if (typeof createImageBitmap === 'function') {
    let bitmap: ImageBitmap | null = null;
    try {
      bitmap = await createImageBitmap(file, {
        imageOrientation: 'from-image',
      } as ImageBitmapOptions);
      return drawToDataUrl(bitmap, bitmap.width, bitmap.height);
    } catch {
      // Fall through to the <img> path below.
    } finally {
      bitmap?.close();
    }
  }

  const objectUrl = URL.createObjectURL(file);
  try {
    const img = await loadImageElement(objectUrl);
    const dataUrl = drawToDataUrl(img, img.naturalWidth, img.naturalHeight);
    URL.revokeObjectURL(objectUrl);
    return dataUrl;
  } catch {
    // Last resort: hand back the live object URL (kept alive for display;
    // the store revokes it on removal).
    return objectUrl;
  }
}
